import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
import api from "../../services/api";
import { useTranslation } from "react-i18next";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ["places"];

const fieldStyle = {
  width: "100%", height: "44px", padding: "0 14px",
  borderRadius: "8px", border: "1px solid #d2d6da",
  fontSize: "14px", color: "#344767", outline: "none",
  background: "#fff", boxSizing: "border-box",
  transition: "border-color 0.2s", fontFamily: "inherit",
};

function Section({ title, icon, children }) {
  return (
    <MDBox mb={4}>
      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
        <MDBox sx={{
          width: 32, height: 32, borderRadius: "8px",
          background: "linear-gradient(135deg, #6a1b9a, #7b1fa2)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon sx={{ color: "#fff", fontSize: 16 }}>{icon}</Icon>
        </MDBox>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767" }}>
          {title}
        </MDTypography>
      </MDBox>
      <Divider sx={{ mb: 3 }} />
      {children}
    </MDBox>
  );
}

function FieldLabel({ label, required, children }) {
  return (
    <MDBox mb={2}>
      <MDTypography variant="caption" fontWeight="bold" color="text" sx={{
        mb: 0.5, display: "block", textTransform: "uppercase",
        fontSize: 11, letterSpacing: "0.05em",
      }}>
        {label}{required && " *"}
      </MDTypography>
      {children}
    </MDBox>
  );
}

function InputField({ label, name, value, onChange, type = "text", placeholder, multiline, rows }) {
  if (multiline) return (
    <FieldLabel label={label}>
      <textarea name={name} value={value || ""} onChange={onChange}
        placeholder={placeholder} rows={rows || 4} autoComplete="off"
        style={{ ...fieldStyle, height: "auto", padding: "10px 14px", resize: "vertical" }}
        onFocus={(e) => (e.target.style.borderColor = "#6a1b9a")}
        onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
      />
    </FieldLabel>
  );
  return (
    <FieldLabel label={label}>
      <input name={name} type={type} value={value || ""} onChange={onChange}
        placeholder={placeholder} autoComplete="off" style={fieldStyle}
        onFocus={(e) => (e.target.style.borderColor = "#6a1b9a")}
        onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
      />
    </FieldLabel>
  );
}

function EnseignantForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isEdit = Boolean(id && id !== "nouveau");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Webcam
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [stream, setStream] = useState(null);

  // Google Maps Autocomplete
  const autocompleteRef = useRef(null);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const [form, setForm] = useState({
    nom: "", prenom: "", adresse: "", ville: "", code_postal: "",
    telephone: "", email: "", mot_de_passe: "",
    latitude: "", longitude: "",
  });

  useEffect(() => {
    if (isEdit) {
      api.get(`/enseignants/${id}`).then(({ data }) => {
        setForm({ ...data, mot_de_passe: "" });
        if (data.photo) setPhotoPreview(`${data.photo}`);
      });
    }
  }, [id, isEdit]);

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      const components = place.address_components || [];
      const get = (type) =>
        components.find((c) => c.types.includes(type))?.long_name || "";
      const streetNumber = get("street_number");
      const route = get("route");
      const adresse = [streetNumber, route].filter(Boolean).join(" ");
      const ville = get("locality") || get("administrative_area_level_2");
      const code_postal = get("postal_code");
      const lat = place.geometry?.location?.lat() || "";
      const lng = place.geometry?.location?.lng() || "";
      setForm((prev) => ({ ...prev, adresse, ville, code_postal, latitude: lat, longitude: lng }));
    }
  };

  const startWebcam = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(s);
      setShowWebcam(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = s; }, 100);
    } catch { alert(t("commun.photo.erreurWebcam")); }
  };

  const stopWebcam = () => {
    if (stream) stream.getTracks().forEach((t) => t.stop());
    setShowWebcam(false);
    setStream(null);
  };

  const takePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
      setPhotoFile(file);
      setPhotoPreview(canvas.toDataURL("image/jpeg"));
    }, "image/jpeg");
    stopWebcam();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) { setPhotoFile(file); setPhotoPreview(URL.createObjectURL(file)); }
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.prenom.trim()) {
      setError(t("enseignants.erreurPrenom"));
      return;
    }
    setError("");
    setSaving(true);

    const cleanForm = Object.fromEntries(
      Object.entries(form).map(([key, val]) => {
        if (val === "" || val === undefined) return [key, null];
        return [key, val];
      })
    );

    try {
      let savedId = id;
      if (isEdit) {
        // Ne pas envoyer mot_de_passe si vide
        if (!cleanForm.mot_de_passe) delete cleanForm.mot_de_passe;
        await api.put(`/enseignants/${id}`, cleanForm);
      } else {
        const { data } = await api.post("/enseignants", cleanForm);
        savedId = data.id;
      }
      if (photoFile) {
        const fd = new FormData();
        fd.append("photo", photoFile);
        await api.post(`/enseignants/${savedId}/photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      navigate("/enseignants");
    } catch (err) {
      setError(err.response?.data?.message || t("commun.erreurEnregistrement"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={10}>

            <MDBox mb={3} display="flex" alignItems="center" gap={2}>
              <MDButton variant="outlined" color="secondary" size="small"
                onClick={() => navigate("/enseignants")}
                sx={{ borderRadius: "10px", px: 2, whiteSpace: "nowrap" }}>
                <Icon sx={{ mr: 0.5 }}>arrow_back</Icon> {t("commun.retourListe")}
              </MDButton>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#344767" }}>
                {isEdit ? t("enseignants.titreModifier") : t("enseignants.ajouter")}
              </MDTypography>
            </MDBox>

            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
              <MDBox mx={2} mt={-3} py={2} px={3} sx={{
                background: "linear-gradient(135deg, #6a1b9a 0%, #7b1fa2 100%)",
                borderRadius: "12px", boxShadow: "0 4px 20px rgba(106,27,154,0.4)",
              }}>
                <MDTypography variant="h6" color="white" fontWeight="bold">
                  {t("enseignants.bandeau")}
                </MDTypography>
              </MDBox>

              <MDBox pt={4} pb={4} px={4}>
                {error && (
                  <MDBox mb={3} p={2}
                    sx={{ background: "#ffebee", borderRadius: "10px", border: "1px solid #ffcdd2" }}>
                    <MDTypography variant="caption" color="error" fontWeight="medium">
                      ⚠️ {error}
                    </MDTypography>
                  </MDBox>
                )}

                <MDBox component="form" onSubmit={handleSubmit} autoComplete="off">

                  {/* ── SECTION 1 : PHOTO ── */}
                  <Section title={t("commun.photo.titre")} icon="camera_alt">
                    <MDBox display="flex" alignItems="center" gap={3} flexWrap="wrap">
                      <MDBox sx={{
                        width: 110, height: 110, borderRadius: "50%",
                        border: "3px solid #f3e5f5", overflow: "hidden",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: "#fdf8ff", flexShrink: 0,
                      }}>
                        {photoPreview ? (
                          <img src={photoPreview} alt={t("commun.photo.apercu")}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <Icon style={{ fontSize: 50, color: "#ce93d8" }}>person</Icon>
                        )}
                      </MDBox>
                      <MDBox display="flex" flexDirection="column" gap={1}>
                        <MDButton variant="outlined" size="small" onClick={startWebcam}
                          sx={{ color: "#6a1b9a", borderColor: "#6a1b9a" }}>
                          <Icon>camera_alt</Icon>&nbsp; {t("commun.photo.webcam")}
                        </MDButton>
                        <MDButton variant="outlined" color="secondary" size="small" component="label">
                          <Icon>upload</Icon>&nbsp; {t("commun.photo.uploader")}
                          <input type="file" accept="image/*" hidden onChange={handleFileUpload} />
                        </MDButton>
                        {photoPreview && (
                          <MDButton variant="outlined" color="error" size="small"
                            onClick={() => { setPhotoPreview(null); setPhotoFile(null); }}>
                            <Icon>cancel</Icon>&nbsp; {t("commun.photo.supprimer")}
                          </MDButton>
                        )}
                      </MDBox>
                    </MDBox>
                    {showWebcam && (
                      <MDBox mt={2}>
                        <video ref={videoRef} autoPlay
                          style={{ width: "100%", maxWidth: 400, borderRadius: 10 }} />
                        <canvas ref={canvasRef} style={{ display: "none" }} />
                        <MDBox display="flex" gap={2} mt={1}>
                          <MDButton variant="gradient" color="success" size="small" onClick={takePhoto}>
                            <Icon>camera</Icon>&nbsp; {t("commun.photo.prendre")}
                          </MDButton>
                          <MDButton variant="outlined" color="error" size="small" onClick={stopWebcam}>
                            {t("commun.annuler")}
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    )}
                  </Section>

                  {/* ── SECTION 2 : IDENTITÉ ── */}
                  <Section title={t("eleves.sections.identite")} icon="person">
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FieldLabel label={`${t("champs.prenom")} *`}>
                          <input name="prenom" value={form.prenom} onChange={handleChange}
                            placeholder={t("enseignants.prenomPlaceholder")} autoComplete="off"
                            style={fieldStyle}
                            onFocus={(e) => (e.target.style.borderColor = "#6a1b9a")}
                            onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
                          />
                        </FieldLabel>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <InputField label={t("champs.nom")} name="nom"
                          value={form.nom} onChange={handleChange}
                          placeholder={t("enseignants.nomPlaceholder")} />
                      </Grid>
                    </Grid>
                  </Section>

                  {/* ── SECTION 3 : COORDONNÉES ── */}
                  <Section title={t("eleves.sections.coordonnees")} icon="contact_phone">
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FieldLabel label={t("champs.adressePostale")}>
                          {isLoaded ? (
                            <Autocomplete
                              onLoad={(ref) => (autocompleteRef.current = ref)}
                              onPlaceChanged={onPlaceChanged}
                              options={{ componentRestrictions: { country: "fr" }, types: ["address"] }}
                            >
                              <input name="adresse" value={form.adresse || ""} onChange={handleChange}
                                placeholder={t("commun.adresse.placeholder")} autoComplete="off"
                                style={fieldStyle}
                                onFocus={(e) => (e.target.style.borderColor = "#6a1b9a")}
                                onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
                              />
                            </Autocomplete>
                          ) : (
                            <input name="adresse" value={form.adresse || ""} onChange={handleChange}
                              placeholder={t("champs.adressePostale")} autoComplete="off" style={fieldStyle} />
                          )}
                        </FieldLabel>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <InputField label={t("champs.ville")} name="ville"
                          value={form.ville} onChange={handleChange} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <InputField label={t("champs.codePostal")} name="code_postal"
                          value={form.code_postal} onChange={handleChange} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <InputField label={t("champs.telephone")} name="telephone"
                          value={form.telephone} onChange={handleChange} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FieldLabel label={t("champs.email")}>
                          <input name="email" type="text" value={form.email || ""}
                            onChange={handleChange} placeholder="exemple@email.com"
                            autoComplete="off" style={fieldStyle}
                            onFocus={(e) => (e.target.style.borderColor = "#6a1b9a")}
                            onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
                          />
                        </FieldLabel>
                      </Grid>
                    </Grid>
                  </Section>

                  {/* ── SECTION 4 : ACCÈS ── */}
                  <Section title={t("enseignants.sectionAcces")} icon="lock">
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FieldLabel label={isEdit ? t("commun.motDePasse.nouveau") : `${t("champs.motDePasse")} *`}>
                          <input name="mot_de_passe" type="password" value={form.mot_de_passe || ""}
                            onChange={handleChange}
                            placeholder={isEdit ? t("commun.motDePasse.laisserVide") : t("commun.motDePasse.min8")}
                            autoComplete="new-password" style={fieldStyle}
                            onFocus={(e) => (e.target.style.borderColor = "#6a1b9a")}
                            onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
                          />
                        </FieldLabel>
                      </Grid>
                    </Grid>
                    <MDBox mt={1} p={2} sx={{ background: "#f3e5f5", borderRadius: "10px" }}>
                      <MDTypography variant="caption" sx={{ color: "#6a1b9a" }}>
                        ℹ️ {t("enseignants.infoConnexion")}
                      </MDTypography>
                    </MDBox>
                  </Section>

                  {/* ── BOUTONS ── */}
                  <MDBox display="flex" gap={2} justifyContent="flex-end" mt={2}>
                    <MDButton variant="outlined" color="secondary"
                      onClick={() => navigate("/enseignants")}>
                      {t("commun.annuler")}
                    </MDButton>
                    <MDButton type="submit" disabled={saving} sx={{
                      px: 4, background: "linear-gradient(135deg, #6a1b9a, #7b1fa2)",
                      color: "#fff", borderRadius: "8px", fontWeight: 700,
                      "&:hover": { background: "#4a148c" },
                    }}>
                      {saving ? t("commun.enregistrement") : isEdit ? t("commun.mettreAJour") : t("enseignants.creer")}
                    </MDButton>
                  </MDBox>

                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EnseignantForm;